import { Badge, Box, Flex, Input, Tabs, Text } from '@chakra-ui/react'
import { Search } from 'lucide-react'
import React, { useCallback, useState } from 'react'

import { useReportingI18n } from '../../../i18n'
import type {
  QueueAgent,
  QueueDashboardAgentActions,
  QueueDashboardCustomStatus,
  QueueDashboardExtension,
} from '../types'
import ActiveAgentsTable from './ActiveAgentsTable'
import InactiveAgentsTable from './InactiveAgentsTable'

export interface AgentsPanelProps {
  activeAgents: QueueAgent[]
  inactiveAgents: QueueAgent[]
  isLoading: boolean
  onRefresh?: () => void
  customStatuses?: QueueDashboardCustomStatus[]
  extensions?: QueueDashboardExtension[]
  isAdmin?: boolean
  onQueueLogin?: QueueDashboardAgentActions['onQueueLogin']
  onQueueLogout?: QueueDashboardAgentActions['onQueueLogout']
  onUpdateAgentStatus?: QueueDashboardAgentActions['onUpdateAgentStatus']
  onQueueUpdateError?: QueueDashboardAgentActions['onQueueUpdateError']
  onStatusUpdated?: QueueDashboardAgentActions['onStatusUpdated']
}

type AgentTab = 'active' | 'inactive'

const AgentsPanel: React.FC<AgentsPanelProps> = ({
  activeAgents,
  inactiveAgents,
  isLoading,
  onRefresh,
  customStatuses = [],
  extensions = [],
  isAdmin = false,
  onQueueLogin,
  onQueueLogout,
  onUpdateAgentStatus,
  onQueueUpdateError,
  onStatusUpdated,
}) => {
  const { text } = useReportingI18n()
  const [activeTab, setActiveTab] = useState<AgentTab>('active')
  const [searchQuery, setSearchQuery] = useState('')

  const agentActions = {
    onQueueLogin,
    onQueueLogout,
    onUpdateAgentStatus,
    onQueueUpdateError,
    onStatusUpdated: () => {
      onStatusUpdated?.()
      onRefresh?.()
    },
  }

  const normalizeSearchQuery = (query: string): string => {
    return query.toLowerCase().trim()
  }

  const matchesAgent = (agent: QueueAgent, query: string): boolean => {
    if (!query) return true

    const normalizedQuery = normalizeSearchQuery(query)
    const normalizedName = agent.name.toLowerCase()
    const normalizedExtension = agent.extension.toLowerCase()

    return (
      normalizedName.includes(normalizedQuery) ||
      normalizedExtension.includes(normalizedQuery)
    )
  }

  const filteredActiveAgents = activeAgents.filter((agent) =>
    matchesAgent(agent, searchQuery),
  )

  const filteredInactiveAgents = inactiveAgents.filter((agent) =>
    matchesAgent(agent, searchQuery),
  )

  const handleTabChange = useCallback((details: { value: string }) => {
    if (details.value === 'active' || details.value === 'inactive') {
      setActiveTab(details.value)
    }
  }, [])

  return (
    <Box
      bg="bg.panel"
      borderRadius="8px"
      border="1px solid"
      borderColor="border.default"
      overflow="hidden"
      data-testid="agents-panel"
    >
      <Tabs.Root
        value={activeTab}
        onValueChange={handleTabChange}
        variant="plain"
      >
        <Flex
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          gap="12px"
          padding="16px"
          css={{
            // Container query: switch to row layout when container is wide enough
            '@container queue-dashboard (min-width: 950px)': {
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
            },
          }}
        >
          <Text
            fontSize="1rem"
            fontWeight="700"
            color="fg.default"
            flexShrink={0}
          >
            {text('queueDashboard.agentsPanel.title', 'Agent Queue')}
          </Text>

          <Flex
            direction="column"
            alignItems="flex-start"
            gap="12px"
            css={{
              // Container query: switch to row layout at smaller breakpoint
              '@container queue-dashboard (min-width: 950px)': {
                flexDirection: 'row',
                alignItems: 'center',
                gap: '24px',
              },
            }}
          >
            <Tabs.List
              bg="bg.panel"
              borderRadius="8px"
              padding="4px"
              gap="0"
              border="1px solid"
              borderColor="border.default"
              width="fit-content"
            >
              <Tabs.Trigger
                value="active"
                px="12px"
                py="6px"
                fontSize="0.875rem"
                fontWeight="500"
                borderRadius="8px"
                color="fg.muted"
                _selected={{
                  bg: 'bg.muted',
                  color: 'fg.default',
                }}
              >
                <Badge
                  bg="blue.solid"
                  color="blue.contrast"
                  borderRadius="4px"
                  px="8px"
                  py="0"
                  fontSize="0.75rem"
                  mr="8px"
                >
                  {activeAgents.length}
                </Badge>
                {text(
                  'queueDashboard.agentsPanel.activeAgents',
                  'Active Agents',
                )}
              </Tabs.Trigger>
              <Tabs.Trigger
                value="inactive"
                px="12px"
                py="6px"
                fontSize="0.875rem"
                fontWeight="500"
                borderRadius="8px"
                color="fg.muted"
                _selected={{
                  bg: 'bg.muted',
                  color: 'fg.default',
                }}
              >
                <Badge
                  bg="gray.emphasized"
                  color="gray.contrast"
                  borderRadius="4px"
                  px="8px"
                  py="0"
                  fontSize="0.75rem"
                  mr="8px"
                >
                  {inactiveAgents.length}
                </Badge>
                {text(
                  'queueDashboard.agentsPanel.inactiveAgents',
                  'Inactive Agents',
                )}
              </Tabs.Trigger>
            </Tabs.List>

            <Flex alignItems="center" gap="12px">
              <Box position="relative">
                <Box
                  as={Search}
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="fg.muted"
                  width="16px"
                  height="16px"
                />
                <Input
                  placeholder={text(
                    'queueDashboard.agentsPanel.searchPlaceholder',
                    'Enter name or number',
                  )}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  pl="36px"
                  size="sm"
                  width="250px"
                  borderRadius="6px"
                  borderColor="border.default"
                  _placeholder={{ color: 'fg.muted' }}
                />
              </Box>
              {/* TODO: Add export CSV button - disabled for now
            <Button
              size="sm"
              colorPalette="brand"
              borderRadius="md"
              onClick={handleExportCSV}
            >
              <Icon as={Download} boxSize="14px" color="blue.contrast" />
              {text('queueDashboard.agentsPanel.exportCsv', 'Export CSV')}
            </Button> */}
            </Flex>
          </Flex>
        </Flex>

        <Tabs.ContentGroup>
          <Tabs.Content value="active" padding="16px">
            <Box
              borderRadius="md"
              overflowX="auto"
              border="1px solid"
              borderColor="border.default"
              maxHeight="500px"
              overflowY="auto"
            >
              <ActiveAgentsTable
                agents={filteredActiveAgents}
                isLoading={isLoading}
                customStatuses={customStatuses}
                extensions={extensions}
                showActions={isAdmin}
                agentActions={agentActions}
              />
            </Box>
          </Tabs.Content>
          <Tabs.Content value="inactive" padding="16px">
            <Box
              borderRadius="md"
              overflowX="auto"
              border="1px solid"
              borderColor="border.default"
              maxHeight="500px"
              overflowY="auto"
            >
              <InactiveAgentsTable
                agents={filteredInactiveAgents}
                isLoading={isLoading}
                customStatuses={customStatuses}
                extensions={extensions}
                showActions={isAdmin}
                agentActions={agentActions}
              />
            </Box>
          </Tabs.Content>
        </Tabs.ContentGroup>
      </Tabs.Root>
    </Box>
  )
}

export default AgentsPanel
